import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  MAX_QUICK_REPLIES,
  MAX_QUICK_REPLY_LENGTH,
  useTranslation,
} from "hhn-commons";
import React, { useState } from "react";

interface QuickReplyAddProps {
  onAdd: (reply: string) => Promise<boolean>;
  isLoading: boolean;
  quickRepliesCount: number;
}

const QuickReplyAdd = ({
  onAdd,
  isLoading,
  quickRepliesCount,
}: QuickReplyAddProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "quickReply" });
  const [input, setInput] = useState("");

  const isLimitReached = quickRepliesCount >= MAX_QUICK_REPLIES;
  const isCharacterLimitExceeded = input.length > MAX_QUICK_REPLY_LENGTH;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const reply = input.trim();
    if (!reply || isLimitReached || isCharacterLimitExceeded) return;

    const isAdded = await onAdd(reply);
    if (isAdded) setInput("");
  };

  const getHelperText = () => {
    if (isLimitReached) return tNs("addLimitError");
    if (isCharacterLimitExceeded) return tNs("characterLimitError");
    return undefined;
  };

  return (
    <div style={{ width: "100%" }}>
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
      >
        <TextField
          label={tNs("addQuickReplyLabel")}
          variant="outlined"
          value={input}
          onChange={e => setInput(e.target.value)}
          fullWidth
          size="small"
          style={{ flex: 1 }}
          disabled={isLimitReached}
          error={isLimitReached || isCharacterLimitExceeded}
          helperText={getHelperText()}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="small"
          disabled={
            isLoading ||
            !input.trim() ||
            isLimitReached ||
            isCharacterLimitExceeded
          }
          sx={{ height: "38px" }}
        >
          {tNs("addButton")}
        </Button>
      </form>
      <Divider style={{ margin: "16px 0" }} />
      <Typography variant="body2" align="right" sx={{
        color: "text.secondary"
      }}>
        {quickRepliesCount}/{MAX_QUICK_REPLIES}
      </Typography>
    </div>
  );
};

export default QuickReplyAdd;
